import { useCallback, useEffect, useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { planTiers } from "@/react/helpers/planHelpers";
import { useChecklistStore } from "@/react/stores/useChecklistStore";
import {
  type CommunityAccess,
  communityAccessOptions,
} from "@/react/types/Checklist";
import { communityApi } from "@circle-react/api";
import { usePunditUserContext } from "@circle-react/contexts";
import { usePlanStatus } from "@circle-react/hooks/usePlanStatus";
import { useUpgradePlanModal } from "@circle-react-shared/UpgradePlan";

const localeRoot = "community_onboarding.new_checklist.confirm_access_section";

export const useConfirmAccess = () => {
  const { currentCommunity } = usePunditUserContext();
  const { isCommunityOnTrial } = usePlanStatus();
  const upgradePlanModal = useUpgradePlanModal();
  const { public: publicAccess, private: privateAccess } =
    communityAccessOptions;

  const { communityAccess, setCommunityAccess } = useChecklistStore();

  useEffect(() => {
    const initialAccess = currentCommunity?.is_private
      ? privateAccess
      : publicAccess;
    setCommunityAccess(initialAccess);
  }, [
    currentCommunity?.is_private,
    setCommunityAccess,
    privateAccess,
    publicAccess,
  ]);

  const handleAccessChange = (value: CommunityAccess) => {
    setCommunityAccess(value);
  };

  const shouldUpgrade = useMemo(
    () => isCommunityOnTrial && communityAccess === publicAccess,
    [isCommunityOnTrial, communityAccess, publicAccess],
  );

  const confirmAccess = useCallback(async () => {
    if (shouldUpgrade) {
      const result = await upgradePlanModal.show({
        planTier: planTiers.professional,
        showPlanDetails: true,
        source: "checklist_confirm_access",
        subheading: t(`${localeRoot}.public.modal_subheading`),
      });

      if (!result) {
        return null;
      }
    }

    return communityApi.update({
      community: {
        is_private: communityAccess === privateAccess,
      },
    });
  }, [upgradePlanModal, communityAccess, shouldUpgrade, privateAccess]);

  return {
    communityAccess,
    handleAccessChange,
    confirmAccess,
  };
};
